import React, { useState, useEffect } from 'react'
import { Modal } from '../ui/modal'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { Loader2, Key } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'

interface SignupPin {
  id: string
  pin: string
  created_at: string
  used_at: string | null
  used_by: string | null
  expires_at: string
}

interface PinManagementModalProps {
  open: boolean
  onClose: () => void
}

export function PinManagementModal({ open, onClose }: PinManagementModalProps) {
  const [pins, setPins] = useState<SignupPin[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [expiryDays, setExpiryDays] = useState('7')
  const [isCreating, setIsCreating] = useState(false)
  const [filter, setFilter] = useState<'all' | 'available' | 'used'>('all')

  useEffect(() => {
    if (open) {
      loadPins()
    }
  }, [open])

  const loadPins = async () => {
    try {
      const { data, error } = await supabase
        .from('signup_pins')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setPins(data)
    } catch (error) {
      console.error('Error loading PINs:', error)
      toast.error('Failed to load signup PINs')
    } finally {
      setIsLoading(false)
    }
  }

  const generatePin = () => {
    return Math.floor(100000 + Math.random() * 900000).toString()
  }

  const createPin = async () => {
    setIsCreating(true)
    try {
      const expiryDate = new Date()
      expiryDate.setDate(expiryDate.getDate() + parseInt(expiryDays))

      const { error } = await supabase
        .from('signup_pins')
        .insert({
          pin: generatePin(),
          expires_at: expiryDate.toISOString(),
          created_by: (await supabase.auth.getUser()).data.user?.id
        })

      if (error) throw error

      toast.success('Signup PIN created successfully')
      loadPins()
    } catch (error) {
      console.error('Error creating PIN:', error)
      toast.error('Failed to create signup PIN')
    } finally {
      setIsCreating(false)
    }
  }

  const filteredPins = pins.filter(pin => {
    const now = new Date()
    const isExpired = new Date(pin.expires_at) < now
    
    switch (filter) {
      case 'available':
        return !pin.used_by && !isExpired
      case 'used':
        return !!pin.used_by
      default:
        return true
    }
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Manage Signup PINs"
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1 space-y-2">
            <label htmlFor="expiryDays" className="text-sm font-medium">
              Days to Expire
            </label>
            <Input
              id="expiryDays"
              type="number"
              min="1"
              max="30"
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value)}
              className="w-32"
            />
          </div>
          <Button 
            onClick={createPin}
            disabled={isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Key className="mr-2 h-4 w-4" />
                Create PIN
              </>
            )}
          </Button>
        </div>

        <Tabs value={filter} onValueChange={(value) => setFilter(value as typeof filter)}>
          <TabsList className="w-full">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="available">Available</TabsTrigger>
            <TabsTrigger value="used">Used</TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPins.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No {filter === 'all' ? '' : filter} PINs found
              </p>
            ) : (
              <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                {filteredPins.map((pin) => (
                  <div key={pin.id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-mono text-lg font-semibold">
                          {pin.pin}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Created: {new Date(pin.created_at).toLocaleString()}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Expires: {new Date(pin.expires_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-right">
                        {pin.used_by ? (
                          <div>
                            <span className="text-sm text-muted-foreground">Used on:</span>
                            <div className="text-sm">
                              {new Date(pin.used_at!).toLocaleString()}
                            </div>
                          </div>
                        ) : new Date(pin.expires_at) < new Date() ? (
                          <span className="text-sm text-destructive">Expired</span>
                        ) : (
                          <span className="text-sm text-primary">Available</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}